/**
 * 勘定科目の一括取り込み(2026-10-23)。Excel「Gate と v8 の勘定科目の違い」の「取り込み用」シートを貼り付けて、
 * 科目①・科目②をまとめて追加する。既存の科目は変えない(変えるときは科目①の編集で)。判定は accountImport.ts
 */
import { useMemo, useState } from 'react';
import Modal from '@core/components/Modal';
import { useToast } from '@core/components/Toast';
import { splitPasted } from '@core/budget/budgetImport';
import { useMasters } from './MastersProvider';
import { upsertAccount, upsertSubAccount } from './masterRepo';
import { IMPORT_HEADERS, checkAccountImport, type AccountImportRow } from './accountImport';
import type { DepartmentId } from '@core/lib/types';

const STATUS_LABEL: Record<AccountImportRow['status'], string> = { new: '追加', exists: '既にある', error: 'エラー' };
const STATUS_BADGE: Record<AccountImportRow['status'], string> = { new: 'b-ok', exists: 'b-plan', error: 'b-ng' };

export default function AccountImportModal({ onClose }: { onClose: () => void }) {
  const { accounts, subAccounts, departments, reload } = useMasters();
  const toast = useToast();
  const [text, setText] = useState('');
  const [result, setResult] = useState<{ rows: AccountImportRow[]; error: string | null } | null>(null);
  const [busy, setBusy] = useState(false);

  const targets = useMemo(() => (result?.rows ?? []).filter((r) => r.status === 'new'), [result]);
  const counts = useMemo(() => {
    const c = { accounts: 0, subs: 0, exists: 0, error: 0 };
    for (const r of result?.rows ?? []) {
      if (r.account) c.accounts++;
      c.subs += r.subAccounts.length;
      if (r.status === 'exists') c.exists++;
      if (r.status === 'error') c.error++;
    }
    return c;
  }, [result]);

  function check() {
    if (!text.trim()) {
      toast('Excel の「取り込み用」シートを見出しごと貼り付けてください');
      return;
    }
    const allDepartmentIds = departments.filter((d) => d.id !== 'sonota').map((d) => d.id as DepartmentId);
    setResult(checkAccountImport({ table: splitPasted(text), accounts, subAccounts, allDepartmentIds }));
  }

  async function copyHeaders() {
    try {
      await navigator.clipboard.writeText(IMPORT_HEADERS.join('\t'));
      toast('見出しをコピーしました。シートの1行目に貼り付けてください');
    } catch {
      toast('コピーできませんでした');
    }
  }

  async function doImport() {
    if (targets.length === 0) return;
    const ok = window.confirm(`科目①${counts.accounts}件・科目②${counts.subs}件を追加します。よろしいですか?(既存の科目は変えません)`);
    if (!ok) return;
    setBusy(true);
    let done = 0;
    try {
      for (const r of targets) {
        if (r.account) await upsertAccount(r.account);
        for (const s of r.subAccounts) await upsertSubAccount(s);
        done++;
      }
      await reload();
      toast(`科目①${counts.accounts}件・科目②${counts.subs}件を追加しました`);
      onClose();
    } catch (e) {
      // 途中で止まったら、どこまで入ったかを伝える(もう一度「内容を確かめる」と、入った分は「既にある」になる)
      await reload().catch(() => undefined);
      setResult(null);
      toast(`${done}行目まで追加して止まりました: ${e instanceof Error ? e.message : '保存に失敗しました'}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} width="min(860px, 94vw)">
      <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
        <h3>勘定科目の一括取り込み</h3>
      </div>
      <p className="hint" style={{ marginBottom: 10 }}>
        列: {IMPORT_HEADERS.join(' / ')}(「区分」「科目①」は必須・列の順番は自由)。科目②は「・」区切り。
        追加されるのは、まだ無い科目①と科目②だけです。既にある科目の名前・区分・PL の行は変えません。
      </p>
      <textarea
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setResult(null);
        }}
        placeholder="Excel の「取り込み用」シート(見出し行を含む)をここに貼り付け"
        rows={6}
        style={{ width: '100%', fontFamily: 'inherit', padding: 8, border: '1px solid var(--line)', borderRadius: 6 }}
      />
      <button className="txn-link" style={{ marginTop: 6 }} onClick={() => void copyHeaders()}>
        見出しをコピー
      </button>

      {result?.error && (
        <p className="hint" style={{ color: 'var(--red)', marginTop: 10 }}>
          {result.error}
        </p>
      )}
      {result && !result.error && (
        <div style={{ marginTop: 14 }}>
          <p style={{ margin: '0 0 8px' }}>
            科目①の追加 {counts.accounts}件 / 科目②の追加 {counts.subs}件 / 既にある {counts.exists}件 / エラー {counts.error}件
          </p>
          <table>
            <thead>
              <tr>
                <th>行</th>
                <th style={{ whiteSpace: 'nowrap' }}>区分</th>
                <th>科目①</th>
                <th style={{ whiteSpace: 'nowrap' }}>判定</th>
                <th>内容</th>
              </tr>
            </thead>
            <tbody>
              {result.rows.map((r) => (
                <tr key={r.line}>
                  <td className="hint">{r.line}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{r.itemCategory || '—'}</td>
                  <td>{r.name || '—'}</td>
                  <td>
                    <span className={`badge ${STATUS_BADGE[r.status]}`}>{STATUS_LABEL[r.status]}</span>
                  </td>
                  <td className="hint">
                    {r.message}
                    {r.subAccounts.length > 0 && `(${r.subAccounts.map((s) => s.name).join('・')})`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="modal-actions">
        <button className="btn-line" onClick={check} disabled={busy}>
          内容を確かめる
        </button>
        <button className="btn-green" onClick={() => void doImport()} disabled={busy || targets.length === 0}>
          {busy ? '追加中…' : `${targets.length}行を追加する`}
        </button>
      </div>
    </Modal>
  );
}
